/**
 * Credits List Component
 * 
 * Displays a filterable list of credits with summary statistics.
 * Supports filtering by party type, status, credit type, etc.
 */

import React, { useState } from 'react';
import {
  Table,
  Card,
  Row,
  Col,
  Statistic,
  Tag,
  Space,
  Select,
  Input,
  Button,
  Typography,
  Tooltip,
  Progress,
  Empty,
} from 'antd';
import {
  DollarOutlined,
  FilterOutlined,
  ReloadOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  StopOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { creditsApi } from '../../services/api';
import CreditEntryForm from './CreditEntryForm';

const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;

// Color constants matching design system
const colors = {
  success: '#169959',
  error: '#C32928',
  warning: '#E6B339',
  info: '#179DB5',
  textSecondary: '#8C8C8C',
};

// Credit status config
const statusConfig: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
  APPROVED: { color: colors.success, icon: <CheckCircleOutlined />, label: 'Available' },
  PARTIAL: { color: colors.warning, icon: <ClockCircleOutlined />, label: 'Partially Applied' },
  EXHAUSTED: { color: colors.info, icon: <CheckCircleOutlined />, label: 'Fully Applied' },
  VOIDED: { color: colors.error, icon: <StopOutlined />, label: 'Voided' },
};

// Credit type labels
const creditTypeLabels: Record<string, string> = {
  CLAIM: 'Claim',
  RETURN: 'Return',
  OVERPAYMENT: 'Overpayment',
  PRICING_ADJUSTMENT: 'Pricing Adj.',
  OTHER: 'Other',
};

// Party type labels
const partyTypeLabels: Record<string, string> = {
  BUYER: 'Buyer',
  SUPPLIER: 'Supplier',
};

interface Credit {
  id: string;
  partyType: string;
  partyName: string;
  creditType: string;
  originalAmount: number;
  remainingAmount: number;
  status: string;
  description?: string;
  referenceNumber?: string;
  createdAt: string;
  originShipment?: {
    id: string;
    reference: string;
  };
  appliedAmount?: number;
  applicationCount?: number;
}

interface CreditsListProps {
  /** Called when a credit is selected for detail view */
  onSelect?: (credit: Credit) => void;
  /** Compact mode for embedding */
  compact?: boolean;
}

const CreditsList: React.FC<CreditsListProps> = ({ onSelect, compact = false }) => {
  // Filter state
  const [filters, setFilters] = useState<{
    partyType?: string;
    status?: string;
    creditType?: string;
    search?: string;
  }>({});

  // Fetch credits
  const {
    data: creditsData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['credits', filters],
    queryFn: async () => {
      const response = await creditsApi.list({
        partyType: filters.partyType as any,
        status: filters.status as any,
        creditType: filters.creditType as any,
        partyName: filters.search,
        limit: 100,
      });
      return response as any;
    },
  });

  // Fetch summary
  const { data: summaryData } = useQuery({
    queryKey: ['credits-summary'],
    queryFn: async () => {
      const response = await creditsApi.getSummary();
      return response as any;
    },
  });

  const credits = creditsData?.credits || [];
  const summary = summaryData || { byStatus: {}, totalAvailable: 0 };

  // Table columns
  const columns: ColumnsType<Credit> = [
    {
      title: 'Reference',
      key: 'reference',
      width: 180,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.referenceNumber || record.id.slice(-8).toUpperCase()}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.originShipment?.reference || 'No shipment'}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Party',
      key: 'party',
      width: 150,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{record.partyName}</Text>
          <Tag color={record.partyType === 'BUYER' ? 'blue' : 'green'}>
            {partyTypeLabels[record.partyType]}
          </Tag>
        </Space>
      ),
      filters: [
        { text: 'Buyer', value: 'BUYER' },
        { text: 'Supplier', value: 'SUPPLIER' },
      ],
      onFilter: (value, record) => record.partyType === value,
    },
    {
      title: 'Type',
      dataIndex: 'creditType',
      key: 'creditType',
      width: 120,
      render: (type) => (
        <Tag>{creditTypeLabels[type] || type}</Tag>
      ),
      filters: Object.entries(creditTypeLabels).map(([value, text]) => ({ text, value })),
      onFilter: (value, record) => record.creditType === value,
    },
    {
      title: 'Amount',
      key: 'amount',
      width: 150,
      align: 'right',
      render: (_, record) => {
        const applied = Number(record.originalAmount) - Number(record.remainingAmount);
        const percentApplied = (applied / Number(record.originalAmount)) * 100;
        
        return (
          <Space direction="vertical" size={0} style={{ width: '100%' }}>
            <Text strong>${Number(record.originalAmount).toFixed(2)}</Text>
            {record.status !== 'VOIDED' && (
              <Tooltip title={`$${applied.toFixed(2)} applied, $${Number(record.remainingAmount).toFixed(2)} remaining`}>
                <Progress
                  percent={percentApplied}
                  size="small"
                  showInfo={false}
                  strokeColor={colors.info}
                  trailColor="#f0f0f0"
                  style={{ width: 80 }}
                />
              </Tooltip>
            )}
          </Space>
        );
      },
      sorter: (a, b) => Number(a.originalAmount) - Number(b.originalAmount),
    },
    {
      title: 'Remaining',
      dataIndex: 'remainingAmount',
      key: 'remainingAmount',
      width: 100,
      align: 'right',
      render: (amount, record) => (
        <Text
          style={{
            color: record.status === 'APPROVED' ? colors.success : 
                   record.status === 'PARTIAL' ? colors.warning : 
                   colors.textSecondary,
          }}
        >
          ${Number(amount).toFixed(2)}
        </Text>
      ),
      sorter: (a, b) => Number(a.remainingAmount) - Number(b.remainingAmount),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => {
        const config = statusConfig[status] ?? { color: colors.textSecondary, icon: null, label: status };
        return (
          <Tag color={config.color} icon={config.icon}>
            {config.label}
          </Tag>
        );
      },
      filters: Object.entries(statusConfig).map(([value, config]) => ({
        text: config.label,
        value,
      })),
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 100,
      render: (date) => dayjs(date).format('MMM D, YYYY'),
      sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
      defaultSortOrder: 'descend',
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => onSelect?.(record)}
        />
      ),
    },
  ];

  // Summary cards
  const renderSummary = () => {
    const byStatus = summary.byStatus || {};
    
    return (
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Available"
              value={summary.totalAvailable || 0}
              precision={2}
              prefix={<DollarOutlined />}
              valueStyle={{ color: colors.success }}
            />
            <Text type="secondary">
              {byStatus.APPROVED?.count || 0} + {byStatus.PARTIAL?.count || 0} credits
            </Text>
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Approved"
              value={byStatus.APPROVED?.count || 0}
              suffix={
                <Text type="secondary" style={{ fontSize: 14 }}>
                  / ${(byStatus.APPROVED?.originalAmount || 0).toFixed(0)}
                </Text>
              }
              valueStyle={{ color: colors.success }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Partially Applied"
              value={byStatus.PARTIAL?.count || 0}
              suffix={
                <Text type="secondary" style={{ fontSize: 14 }}>
                  / ${(byStatus.PARTIAL?.remainingAmount || 0).toFixed(0)} left
                </Text>
              }
              valueStyle={{ color: colors.warning }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Exhausted"
              value={byStatus.EXHAUSTED?.count || 0}
              suffix={
                <Text type="secondary" style={{ fontSize: 14 }}>
                  / ${(byStatus.EXHAUSTED?.originalAmount || 0).toFixed(0)} applied
                </Text>
              }
              valueStyle={{ color: colors.info }}
            />
          </Card>
        </Col>
      </Row>
    );
  };

  // Filter controls
  const renderFilters = () => (
    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
      <Col flex="auto">
        <Space wrap>
          <Search
            placeholder="Search by party name..."
            allowClear
            style={{ width: 200 }}
            onSearch={(value) => setFilters((f) => ({ ...f, search: value || undefined }))}
          />
          <Select
            placeholder="Party Type"
            allowClear
            style={{ width: 120 }}
            onChange={(value) => setFilters((f) => ({ ...f, partyType: value }))}
          >
            <Option value="BUYER">Buyer</Option>
            <Option value="SUPPLIER">Supplier</Option>
          </Select>
          <Select
            placeholder="Status"
            allowClear
            style={{ width: 150 }}
            onChange={(value) => setFilters((f) => ({ ...f, status: value }))}
          >
            {Object.entries(statusConfig).map(([value, config]) => (
              <Option key={value} value={value}>{config.label}</Option>
            ))}
          </Select>
          <Select
            placeholder="Credit Type"
            allowClear
            style={{ width: 150 }}
            onChange={(value) => setFilters((f) => ({ ...f, creditType: value }))}
          >
            {Object.entries(creditTypeLabels).map(([value, label]) => (
              <Option key={value} value={value}>{label}</Option>
            ))}
          </Select>
        </Space>
      </Col>
      <Col>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
            Refresh
          </Button>
          <CreditEntryForm onSuccess={() => refetch()} />
        </Space>
      </Col>
    </Row>
  );

  if (compact) {
    return (
      <Table
        dataSource={credits}
        columns={columns.filter((c) => ['reference', 'party', 'amount', 'status'].includes(c.key as string))}
        rowKey="id"
        size="small"
        loading={isLoading}
        pagination={{ pageSize: 5 }}
        locale={{
          emptyText: <Empty description="No credits found" />,
        }}
      />
    );
  }

  return (
    <div>
      {!compact && renderSummary()}
      {renderFilters()}
      <Table
        dataSource={credits}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total) => `${total} credits`,
        }}
        locale={{
          emptyText: <Empty description="No credits found. Create one to get started." />,
        }}
        onRow={(record) => ({
          onClick: () => onSelect?.(record),
          style: { cursor: 'pointer' },
        })}
      />
    </div>
  );
};

export default CreditsList;
